import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import { type W_Order_FindOrderReportTable_VM_Row } from 'ab-api/web/order/findForOrderReportTable';

import { dollarFormatter } from '@acceligentllc/shared/utils/formatting';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';
import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import { DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';
import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import DateFilter from 'af-components/DateFilter';
import DynamicTooltip from 'af-components/DynamicTooltip';
import ImageTag from 'af-components/Image';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import LinkCell from 'af-components/Table/Cells/LinkCell';
import type { TableProps } from 'af-components/Table/types';

import CLIENT from 'af-routes/client';

import * as LocationActions from 'af-actions/location';
import * as OrderActions from 'af-actions/orders';

import { toCSVData } from './helpers';
import styles from './styles.module.scss';
import type { CategoryFilterOption } from '../Shared/Filters/CategoryFilter';
import CategoryFilter from '../Shared/Filters/CategoryFilter';
import type { CrewTypeFilterOption } from '../Shared/Filters/CrewTypeFilter';
import CrewTypeFilter from '../Shared/Filters/CrewTypeFilter';
import type { DepartmentFilterOption } from '../Shared/Filters/DepartmentFilter';
import DepartmentFilter from '../Shared/Filters/DepartmentFilter';
import type { EquipmentFilterOption } from '../Shared/Filters/EquipmentFilter';
import EquipmentFilter from '../Shared/Filters/EquipmentFilter';
import type { JobFilterOption } from '../Shared/Filters/JobFilter';
import JobFilter from '../Shared/Filters/JobFilter';
import type { LocationFilterOption } from '../Shared/Filters/LocationFilter';
import LocationFilter from '../Shared/Filters/LocationFilter';
import type { OperatorFilterOption } from '../Shared/Filters/OperatorFilter';
import OperatorFilter from '../Shared/Filters/OperatorFilter';
import type { RequestingUserFilterOption } from '../Shared/Filters/RequestingUserFilter';
import RequestingUserFilter from '../Shared/Filters/RequestingUserFilter';

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
}

type Props = ConnectedProps<typeof connector> & SettingProps;

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.FULL_DATE);

};

const DATE_OPTIONS = Object.values(TimePeriodRecurrence).filter((item) => item !== TimePeriodRecurrence.ALL);

const IMAGE_COLUMN_WIDTH = 100;

const PAGE_NUMBER_KEY = SettingsKeys.ORDER_REPORT_PAGE_NUMBER();

const OrderReportsTable: React.FC<Props> = (props) => {
	const { findForTable, period: passedPeriod, companyData, fetchTotalAmount, findAllLocationsWithDepartments } = props;

	const tableRef = React.useRef<TableRef<W_Order_FindOrderReportTable_VM_Row>>(null);

	const [startDate, setStartDate] = React.useState(TimeUtils.positionDate(props.startDate, 'start', 'day'));
	const [endDate, setEndDate] = React.useState(TimeUtils.positionDate(props.endDate, 'end', 'day'));
	const [period, setPeriod] = React.useState(passedPeriod);
	const [filterText, setFilterText] = React.useState('');
	const location = useLocation();

	const [categoryFilter, setCategoryFilter] = React.useState<Nullable<CategoryFilterOption>>(null);
	const [crewTypeFilter, setCrewTypeFilter] = React.useState<Nullable<CrewTypeFilterOption>>(null);
	const [departmentFilter, setDepartmentFilter] = React.useState<Nullable<DepartmentFilterOption>>(null);
	const [equipmentFilter, setEquipmentFilter] = React.useState<Nullable<EquipmentFilterOption>>(null);
	const [jobFilter, setJobFilter] = React.useState<Nullable<JobFilterOption>>(null);
	const [operatorFilter, setOperatorFilter] = React.useState<Nullable<OperatorFilterOption>>(null);
	const [requestingUserFilter, setRequestingUserFilter] = React.useState<Nullable<RequestingUserFilterOption>>(null);
	const [locationFilter, setLocationFilter] = React.useState<Nullable<LocationFilterOption>>(null);

	const [locationOptions, setLocationOptions] = React.useState<LocationFilterOption[]>([]);
	const [departmentOptions, setDepartmentOptions] = React.useState<DepartmentFilterOption[]>([]);

	const tableQuery = tableRef.current?.getTableQuery();

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<W_Order_FindOrderReportTable_VM_Row>> => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;
		const additionalFilters = {
			crewTypeId: crewTypeFilter?.id ?? null,
			departmentId: departmentFilter?.id ?? null,
			equipmentId: equipmentFilter?.id ?? null,
			jobId: jobFilter?.id ?? null,
			locationId: locationFilter?.id ?? null,
			operatorId: operatorFilter?.id ?? null,
			requestingUserId: requestingUserFilter?.id ?? null,
			categoryId: categoryFilter?.id ?? null,
		};

		const result = await findForTable(
			new TableQuery({ page, pageSize, sortBy, filterByText }),
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
			Intl.DateTimeFormat().resolvedOptions().timeZone,
			additionalFilters
		);
		setFilterText(filterByText ?? '');
		return result;
	}, [
		crewTypeFilter?.id,
		departmentFilter?.id,
		equipmentFilter?.id,
		jobFilter?.id,
		locationFilter?.id,
		operatorFilter?.id,
		requestingUserFilter?.id,
		categoryFilter?.id,
		findForTable,
		startDate,
		endDate,
	]);

	const fetchLocationAndDepartments = React.useCallback(async () => {
		const result = await findAllLocationsWithDepartments();

		setLocationOptions(result.locations.map((item) => ({ label: item.nickname, id: item.id })));
		setDepartmentOptions(result.locations
			.flatMap((_location) => _location.departments.map((_item) => ({ label: `${_item.name} (${_location.nickname})`, id: _item.id })))
			.sort((a, b) => a.label.localeCompare(b.label))
		);

	}, [findAllLocationsWithDepartments]);

	React.useEffect(() => {
		fetchLocationAndDepartments();
	}, [fetchLocationAndDepartments]);

	const filterByDate = React.useCallback((_startDate: Date, _endDate: Date) => {
		tableRef.current?.resetPagination();
		setStartDate(TimeUtils.positionDate(_startDate, 'start', 'day'));
		setEndDate(TimeUtils.positionDate(_endDate, 'end', 'day'));
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.ORDER_REPORT_START_DATE(),
			_startDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.ORDER_REPORT_END_DATE(),
			_endDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
	}, []);

	const changePeriod = React.useCallback((_period: TimePeriodRecurrence, _selected: Date) => {
		let _startDate: Date = _selected;
		let _endDate: Date = _selected;

		switch (_period) {
			case TimePeriodRecurrence.MONTHLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'month'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'week'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
				_startDate = TimeUtils.positionDate(_selected, 'start', 'day');
				_endDate = TimeUtils.positionDate(_selected, 'end', 'day');
			default:
				break;
		}

		tableRef.current?.resetPagination();
		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
		SettingsUtils.setItem(SettingsKeys.ORDER_REPORT_PERIOD(), _period, BrowserStorageEnum.LOCAL_STORAGE);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.ORDER_REPORT_START_DATE(),
			_startDate, TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.ORDER_REPORT_END_DATE(),
			_endDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
	}, []);

	const filterByCategory = React.useCallback((category: Nullable<CategoryFilterOption>) => {
		setCategoryFilter(category);
		tableRef.current?.resetPagination();
	}, []);

	const filterByCrewType = React.useCallback((_crewType: Nullable<CrewTypeFilterOption>) => {
		setCrewTypeFilter(_crewType);
		tableRef.current?.resetPagination();
	}, []);

	const filterByDepartment = React.useCallback((department: Nullable<DepartmentFilterOption>) => {
		setDepartmentFilter(department);
		tableRef.current?.resetPagination();
	}, []);

	const filterByEquipment = React.useCallback((equipment: Nullable<EquipmentFilterOption>) => {
		setEquipmentFilter(equipment);
		tableRef.current?.resetPagination();
	}, []);

	const filterByJob = React.useCallback((job: Nullable<JobFilterOption>) => {
		setJobFilter(job);
		tableRef.current?.resetPagination();
	}, []);

	const filterByLocation = React.useCallback((_location: Nullable<LocationFilterOption>) => {
		setLocationFilter(_location);
		tableRef.current?.resetPagination();
	}, []);

	const filterByOperator = React.useCallback((operator: Nullable<OperatorFilterOption>) => {
		setOperatorFilter(operator);
		tableRef.current?.resetPagination();
	}, []);

	const filterByRequestingUser = React.useCallback((requestingUser: Nullable<RequestingUserFilterOption>) => {
		setRequestingUserFilter(requestingUser);
		tableRef.current?.resetPagination();
	}, []);

	const renderSpecializedFilters = React.useCallback(() => {
		return <div className={styles['additional-filters']} >
			<CategoryFilter onChange={filterByCategory} />
			<CrewTypeFilter onChange={filterByCrewType} />
			<DepartmentFilter onChange={filterByDepartment} options={departmentOptions} />
			<EquipmentFilter endDate={endDate} onChange={filterByEquipment} startDate={startDate} />
			<JobFilter onChange={filterByJob} />
			<LocationFilter onChange={filterByLocation} options={locationOptions} />
			<OperatorFilter onChange={filterByOperator} />
			<RequestingUserFilter onChange={filterByRequestingUser} />
		</div>;
	}, [
		filterByCategory,
		filterByCrewType,
		filterByDepartment,
		filterByEquipment,
		filterByJob,
		filterByLocation,
		filterByOperator,
		filterByRequestingUser,
		startDate,
		endDate,
		locationOptions,
		departmentOptions,
	]);

	const renderAdditionalFilter = React.useCallback(() => {
		return (
			<DateFilter
				changePeriod={changePeriod}
				endDate={endDate}
				onChange={filterByDate}
				options={DATE_OPTIONS}
				period={period}
				startDate={startDate}
			/>
		);
	}, [
		changePeriod,
		endDate,
		filterByDate,
		period,
		startDate,
	]);

	const renderLinkCell = React.useCallback((_cell: CellContext<W_Order_FindOrderReportTable_VM_Row, string>) => {

		const { id } = _cell.row.original;
		const base = CLIENT.COMPANY.EQUIPMENT.ITEM.PREVIEW(
			`${id}`,
			location.state.orgAlias,
			companyData.name
		);

		const qs = new URLSearchParams({
			startDate: TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
			endDate: TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
			period: period,
		}).toString();

		const path = `${base}?${qs}`;
		return <LinkCell label={''} path={path} />;
	}, [companyData.name, endDate, location.state.orgAlias, period, startDate]);

	const renderTableHeader = React.useCallback((total: { totalAmount: number; }) => {
		return (
			<div className={styles['table-header']}>
				<span>
					Total Amount:
				</span>
				<b>
					{dollarFormatter.format(total.totalAmount)}
				</b>
			</div>
		);
	}, []);

	const fetchOrderReportTotalAmount = React.useCallback(async () => {
		if (!tableQuery) {
			return { totalAmount: 0 };
		}
		const additionalFilters = {
			crewTypeId: crewTypeFilter?.id ?? null,
			departmentId: departmentFilter?.id ?? null,
			equipmentId: equipmentFilter?.id ?? null,
			jobId: jobFilter?.id ?? null,
			locationId: locationFilter?.id ?? null,
			operatorId: operatorFilter?.id ?? null,
			requestingUserId: requestingUserFilter?.id ?? null,
			categoryId: categoryFilter?.id ?? null,
		};
		const result = await fetchTotalAmount(
			tableQuery,
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
			Intl.DateTimeFormat().resolvedOptions().timeZone,
			additionalFilters
		);

		return result;
	}, [
		tableQuery,
		crewTypeFilter?.id,
		departmentFilter?.id,
		equipmentFilter?.id,
		jobFilter?.id,
		locationFilter?.id,
		operatorFilter?.id,
		requestingUserFilter?.id,
		categoryFilter?.id,
		fetchTotalAmount,
		startDate,
		endDate,
	]);

	// Refresh table header info on table query change
	React.useEffect(() => {
		if (tableRef.current) {
			tableRef.current.refreshTableHeader();
		}
	}, [period, startDate, endDate, filterText]);

	const renderQuantityCell = React.useCallback((cell: CellContext<W_Order_FindOrderReportTable_VM_Row, string>) => {

		const message = <>
			Order Quantity: {parseFloat(`${cell.row.original.orderQuantity}`)}
			<br />
			Tool Repair Quantity: {parseFloat(`${cell.row.original.toolRepairQuantity}`)}
		</>;
		return (
			<DynamicTooltip tooltipContent={message}>{parseFloat(`${cell.row.original.orderQuantity + cell.row.original.toolRepairQuantity}`)}</DynamicTooltip>
		);
	}, []);

	const columns: TableProps<W_Order_FindOrderReportTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'imageUrl',
			accessor: 'imageUrl',
			header: () => <EmptyCell isHeader />,
			cell: (cell) => (
				<ImageTag
					fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
					minSize={BlobStorageImageSizeContainer.SIZE_50X50}
					src={cell.row.original.imageUrl}
					tryOriginal={true}
					tryRoot={true}
				/>
			),
			size: IMAGE_COLUMN_WIDTH,
			enableHiding: true,
		},
		{
			id: 'modelNumber',
			accessor: 'modelNumber',
			header: 'Model Number',
			cell: (cell) => (
				<>{cell.row.original.modelNumber}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'name',
			accessor: 'name',
			header: 'Name',
			cell: (cell) => (
				<>{cell.row.original.name}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'category',
			accessor: 'category',
			header: 'Category',
			cell: (cell) => (
				<>{cell.row.original.category}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'location',
			accessor: 'location',
			header: 'Location',
			cell: (cell) => (
				<>{cell.row.original.location}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'department',
			accessor: 'department',
			header: 'Department',
			cell: (cell) => (
				<>{cell.row.original.department}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'price',
			accessor: 'price',
			header: 'Price',
			cell: (cell) => (
				<>{dollarFormatter.format(cell.row.original.price)}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'quantity',
			accessor: 'quantity',
			header: 'Quantity',
			cell: renderQuantityCell,
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'totalAmount',
			accessor: 'totalAmount',
			header: 'Total Amount',
			cell: (cell) => (
				<>{dollarFormatter.format(cell.row.original.totalAmount)}</>
			),
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'link',
			accessor: 'link',
			header: 'Accessory Preview',
			cell: renderLinkCell,
			enableHiding: true,
		},

	], [renderLinkCell, renderQuantityCell]);

	const onDownloadCSVClick = React.useCallback(async () => {
		if (!tableRef.current) {
			return;
		}

		const result = await fetchRows({ ...tableQuery, pageSize: 10000, page: 0 }); // Basically - get all
		const csvData = toCSVData(result.rows);

		const _csvName = `${companyData.name}_orders_report_${TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY)}_${TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY)}.csv`;

		downloadCSV(csvData, _csvName);
	}, [companyData.name, endDate, fetchRows, startDate, tableQuery]);

	const tableButtons = React.useMemo(() => {
		return [
			{
				type: TableButtonType.EXPORT,
				hasPermission: true,
				onClick: onDownloadCSVClick,
				label: 'Export as CSV',
			},
		];
	}, [onDownloadCSVClick]);

	return (
		<TableNew
			additionalFilter={renderAdditionalFilter}
			buttons={tableButtons}
			columns={columns}
			defaultPageSize={100}
			fetch={fetchRows}
			fetchTableHeaderData={fetchOrderReportTotalAmount}
			hasSearchInput={true}
			offsetHeight={-98}
			pageNumberKey={PAGE_NUMBER_KEY}
			ref={tableRef}
			renderTableHeader={renderTableHeader}
			searchTextKey={SettingsKeys.ORDER_REPORT_SEARCH_TEXT()}
			specializedFilters={renderSpecializedFilters}
			tableName={TableNameEnum.ORDER_REPORT}
		/>
	);
};

const mapStateToProps = (state: RootState) => {
	const { companyData, userData } = state.user;
	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps());

function mapDispatchToProps() {
	return {
		findForTable: OrderActions.findForOrdersReportTable,
		fetchTotalAmount: OrderActions.fetchTotalOrderReportAmount,
		findAllLocationsWithDepartments: LocationActions.findAllWithDepartments,
	};
}

const enhance = compose<React.ComponentType>(
	React.memo,
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.ORDER_REPORT_START_DATE(),
			mappedName: 'startDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.ORDER_REPORT_END_DATE(),
			mappedName: 'endDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.ORDER_REPORT_PERIOD(),
			mappedName: 'period',
			defaultValue: TimePeriodRecurrence.DAILY,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	])),
	connector
);

export default enhance(OrderReportsTable);
